import { useEffect, useRef, useState } from 'react';
import type { Place, Snapshot, SnapPod, SnapSession } from './engine';
import { useWidth } from './LineChart';

const DOT = 14;
const TILE = 30;
const SLOT = 30;
const POD_HEAD = 34;
const POD_FOOT = 24;
const GAP = 10;

interface Pt { x: number; y: number }
interface Layout {
  narrow: boolean;
  height: number;
  inX: number; inY: number; inW: number;
  flyX: number; flyY: number; flyCols: number; flyW: number;
  podX: number; podY: number; podW: number; podCols: number;
  podTop: Map<number, number>;
  noteTop: number;
}

function layout(w: number, snap: FleetSnap): Layout {
  const narrow = w < 760;
  const inW = narrow ? w : 96;
  const flyW = narrow ? w : Math.floor((w - 120) * 0.42);
  const flyX = narrow ? 0 : 120;
  const flyCols = Math.max(4, Math.floor(flyW / TILE));
  const flyRows = Math.ceil(snap.flyPool / flyCols);
  const waiting = snap.sessions.filter((s) => s.place.at === 'waiting').length;
  const inRows = narrow ? 1 + Math.floor(waiting / Math.max(1, Math.floor(w / 20))) : 0;
  const inY = 24;
  const flyY = narrow ? inY + 28 + inRows * 20 + 24 : 24;
  const podX = narrow ? 0 : flyX + flyW + 28;
  const podW = narrow ? w : w - podX;
  const podCols = Math.max(2, Math.floor((podW - 24) / SLOT));
  const podY0 = narrow ? flyY + flyRows * TILE + 32 : 24;
  const podTop = new Map<number, number>();
  let y = podY0;
  for (const p of snap.pods) {
    podTop.set(p.id, y);
    y += POD_HEAD + Math.ceil(p.slots / podCols) * SLOT + POD_FOOT + GAP;
  }
  const noteTop = y;
  if (snap.pods.length && snap.demand.blocked && snap.demand.overflow > 0) y += 48;
  const height = Math.max(narrow ? y : Math.max(flyY + flyRows * TILE, y, 24 + Math.ceil(waiting / 3) * 20 + 60), 180);
  return { narrow, height, inX: 0, inY, inW, flyX, flyY, flyCols, flyW, podX, podY: podY0, podW, podCols, podTop, noteTop };
}

class Seats {
  private by = new Map<number, number>();
  sync(ids: number[], cap: number) {
    const keep = new Set(ids);
    for (const id of [...this.by.keys()]) if (!keep.has(id)) this.by.delete(id);
    const taken = new Set(this.by.values());
    let next = 0;
    for (const id of ids) {
      if (this.by.has(id)) continue;
      while (taken.has(next) && next < cap * 4) next++;
      this.by.set(id, next);
      taken.add(next);
    }
  }
  of(id: number) { return this.by.get(id); }
}

const onPlace = (s: SnapSession, p: Place) => (s.place.at === p.at && (p.at !== 'pod' || (s.place as { pod: number }).pod === (p as { pod: number }).pod)) || (s.movingTo?.at === p.at && (p.at !== 'pod' || (s.movingTo as { pod: number }).pod === (p as { pod: number }).pod));

export type FleetSnap = Pick<Snapshot, 'sessions' | 'pods' | 'flyPool' | 'flyTail' | 'demand'>;

export function Flow({ snap, onKill, onEnd, mobile, why, since }: { snap: FleetSnap; onKill?: (pod: number) => void; onEnd?: (session: number) => void; mobile: boolean; why?: (pod: number) => string | null; since?: (pod: number) => string | null }) {
  const [ref, w] = useWidth<HTMLDivElement>();
  const fly = useRef(new Seats());
  const pods = useRef(new Map<number, Seats>());
  const L = layout(Math.max(w, 320), snap);

  fly.current.sync(snap.sessions.filter((s) => onPlace(s, { at: 'fly' })).map((s) => s.id), snap.flyPool);
  for (const p of snap.pods) {
    let seats = pods.current.get(p.id);
    if (!seats) { seats = new Seats(); pods.current.set(p.id, seats); }
    seats.sync(snap.sessions.filter((s) => onPlace(s, { at: 'pod', pod: p.id })).map((s) => s.id), p.slots);
  }
  for (const id of [...pods.current.keys()]) if (!snap.pods.some((p) => p.id === id)) pods.current.delete(id);

  const flyTile = (i: number): Pt => ({ x: L.flyX + (i % L.flyCols) * TILE + (TILE - DOT) / 2, y: L.flyY + Math.floor(i / L.flyCols) * TILE + (TILE - DOT) / 2 });
  const podSlot = (pod: number, i: number): Pt | null => {
    const top = L.podTop.get(pod);
    if (top === undefined) return null;
    return { x: L.podX + 12 + (i % L.podCols) * SLOT + (SLOT - DOT) / 2, y: top + POD_HEAD + Math.floor(i / L.podCols) * SLOT + (SLOT - DOT) / 2 };
  };
  const waitingIds = snap.sessions.filter((s) => s.place.at === 'waiting').map((s) => s.id);
  const inCols = L.narrow ? Math.max(1, Math.floor(L.inW / 20)) : 3;
  const waitPt = (id: number): Pt => { const i = waitingIds.indexOf(id); return { x: L.inX + 8 + (i % inCols) * 20, y: L.inY + 28 + Math.floor(i / inCols) * 20 }; };
  const where = (s: SnapSession, p: Place): Pt | null => {
    if (p.at === 'fly') { const i = fly.current.of(s.id); return i === undefined ? null : flyTile(i); }
    if (p.at === 'pod') { const i = pods.current.get(p.pod)?.of(s.id); return i === undefined ? null : podSlot(p.pod, i); }
    return waitPt(s.id);
  };
  const entry: Pt = L.narrow ? { x: 8, y: L.inY + 4 } : { x: 8, y: L.inY + 4 };

  const tail = snap.flyTail;
  const flyIds = snap.sessions.filter((s) => onPlace(s, { at: 'fly' })).map((s) => s.id);
  const flyUsed = flyIds.length;
  const seated = new Set(flyIds.map((id) => fly.current.of(id)));
  const tailTiles = new Set<number>();
  for (let i = 0; i < snap.flyPool && tailTiles.size < tail; i++) if (!seated.has(i)) tailTiles.add(i);

  return (
    <div ref={ref} style={{ position: 'relative', height: L.height, width: '100%', overflow: 'hidden' }}>
      <ZoneLabel x={L.inX} y={0} text={`Arriving${waitingIds.length ? ` · ${waitingIds.length} waiting` : ''}`} />
      <ZoneLabel x={L.flyX} y={L.narrow ? L.flyY - 22 : 0} text={`Fly · ${flyUsed} of ${snap.flyPool} machines${tail ? ` · ${tail} idle tail` : ''}`} />
      <ZoneLabel x={L.podX} y={L.narrow ? L.podY - 22 : 0} text={`RunPod · ${snap.pods.filter((p) => p.state !== 'gone').length} pod${snap.pods.length === 1 ? '' : 's'}`} />

      {Array.from({ length: snap.flyPool }, (_, i) => {
        const p = flyTile(i);
        const isTail = tailTiles.has(i);
        return <div key={i} title={isTail ? 'Idle tail: stopped a minute after its session left' : undefined} style={{ position: 'absolute', left: p.x - (TILE - DOT) / 2 + 2, top: p.y - (TILE - DOT) / 2 + 2, width: TILE - 4, height: TILE - 4, borderRadius: 6, background: isTail ? 'var(--fill-08)' : 'var(--fill-04)', border: '1px solid var(--hair)' }} />;
      })}

      {snap.pods.length > 0 && snap.demand.blocked && snap.demand.overflow > 0 && (
        <div style={{ position: 'absolute', left: L.podX, top: L.noteTop, width: L.podW, padding: '10px 14px', borderRadius: 10, border: '1px dashed var(--conf-low)', fontSize: 12.5, color: 'var(--conf-low)' }}>
          No more pods: {snap.demand.blocked}. Raise it in Settings.
        </div>
      )}
      {!snap.pods.length && (
        <div style={{ position: 'absolute', left: L.podX, top: L.podY, width: L.podW, padding: '14px 16px', borderRadius: 10, border: '1px dashed var(--fill-16)', fontSize: 12.5, color: 'var(--ink-62)', lineHeight: 1.5 }}>
          {noPodWhy(snap)}
        </div>
      )}
      {snap.pods.map((p) => <PodCard key={p.id} p={p} x={L.podX} y={L.podTop.get(p.id) ?? 0} w={L.podW} cols={L.podCols} onKill={mobile || !onKill ? undefined : () => onKill(p.id)} why={why?.(p.id)} since={since?.(p.id)} />)}

      <svg width="100%" height={L.height} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        {snap.sessions.filter((s) => s.movingTo).map((s) => {
          const a = where(s, s.place);
          const b = where(s, s.movingTo!);
          if (!a || !b) return null;
          return <line key={s.id} x1={a.x + DOT / 2} y1={a.y + DOT / 2} x2={b.x + DOT / 2} y2={b.y + DOT / 2} stroke={s.emergency ? 'var(--live)' : 'var(--brand)'} strokeWidth={2} strokeDasharray="5 4" className="sim__flow" />;
        })}
      </svg>

      {snap.sessions.map((s) => <SessionDot key={s.id} s={s} at={where(s, s.place) ?? entry} entry={entry} onEnd={onEnd && (() => onEnd(s.id))} />)}
      <style>{`.sim__flow{animation:simflow .8s linear infinite}@keyframes simflow{to{stroke-dashoffset:-18}}@media (prefers-reduced-motion:reduce){.sim__flow{animation:none}.sim__dot{transition:none!important}}`}</style>
    </div>
  );
}

function noPodWhy(snap: FleetSnap): string {
  const d = snap.demand;
  if (d.blocked) return `No pod: ${d.blocked}.`;
  if (d.pressure) return `${d.pressure}: a pod starts at the next decision.`;
  if (d.startIn !== null) return `Demand ${d.overflow.toFixed(1)} is over the start line ${d.startAt.toFixed(1)}: a pod starts in ${Math.ceil(d.startIn)} s if it holds.`;
  return `No pod: demand ${d.overflow.toFixed(1)} is under the start line ${Number.isFinite(d.startAt) ? d.startAt.toFixed(1) : '–'} (a pod pays above ${Number.isFinite(d.breakEven) ? d.breakEven.toFixed(1) : '–'} Multilingual sessions).`;
}

function ZoneLabel({ x, y, text }: { x: number; y: number; text: string }) {
  return <div className="tbl__h" style={{ position: 'absolute', left: x, top: y, whiteSpace: 'nowrap' }}>{text}</div>;
}

function SessionDot({ s, at, entry, onEnd }: { s: SnapSession; at: Pt; entry: Pt; onEnd?: () => void }) {
  const [p, setP] = useState<Pt>(entry);
  useEffect(() => {
    const r = requestAnimationFrame(() => setP(at));
    return () => cancelAnimationFrame(r);
  }, [at.x, at.y]); // eslint-disable-line react-hooks/exhaustive-deps
  const english = s.lang === 'english';
  const file = s.kind === 'file';
  const paused = !!s.paused;
  const edge = s.emergency ? 'var(--live)' : paused ? 'var(--conf-mid)' : 'var(--brand)';
  const title = `${s.lang === 'english' ? 'English' : 'Multilingual'} ${s.kind} session ${s.id}${s.ext ? ` (${s.ext})` : ''} · ${Math.round(s.ageS / 60)} min${paused ? ' · paused for a live session: it goes on from its last committed chunk' : s.served ? '' : ' · not producing text'}${s.pauses ? ` · paused ${s.pauses}×` : ''}${s.movingTo ? ` · moving${s.emergency ? ' (rescue)' : ''}` : ''} ${onEnd ? ' · click to end it' : ''}`;
  return (
    <button type="button" className="sim__dot" title={title} aria-label={title} onClick={onEnd} style={{
      padding: 0, cursor: onEnd ? 'pointer' : 'default',
      position: 'absolute', left: 0, top: 0, width: DOT, height: DOT, transform: `translate(${p.x}px, ${p.y}px)`,
      transition: 'transform .7s cubic-bezier(.4,0,.2,1), opacity .3s',
      borderRadius: file ? 3 : '50%',
      background: english ? 'transparent' : paused ? 'var(--conf-mid)' : 'var(--brand)',
      border: `2px ${s.served || paused ? 'solid' : 'dashed'} ${edge}`,
      boxShadow: s.emergency ? '0 0 0 3px var(--live-soft)' : paused ? '0 0 0 3px var(--fill-08)' : undefined,
      opacity: s.served || s.place.at === 'waiting' ? 1 : 0.55,
    }} />
  );
}

const STATE: Record<SnapPod['state'], [string, string]> = {
  creating: ['creating', 'var(--brand)'],
  booting: ['booting', 'var(--brand)'],
  ready: ['ready', 'var(--ok)'],
  draining: ['draining', 'var(--conf-mid)'],
  stopped: ['stopped (disk kept)', 'var(--ink-62)'],
  gone: ['terminated', 'var(--ink-45)'],
};

function PodCard({ p, x, y, w, cols, onKill, why, since }: { p: SnapPod; x: number; y: number; w: number; cols: number; onKill?: () => void; why?: string | null; since?: string | null }) {
  const rows = Math.ceil(p.slots / cols);
  const h = POD_HEAD + rows * SLOT + POD_FOOT;
  const [label, fg] = STATE[p.state];
  const gone = p.state === 'gone' || p.state === 'stopped';
  const note = p.crashed && p.state !== 'gone' ? 'host died: no heartbeats' : p.broken && p.state === 'creating' ? 'broken host: no container' : p.state === 'creating' || p.state === 'booting' ? `${Math.round(p.progress * 100)}% · ${Math.round(p.stateForS)} s` : p.speechOnly ? `voices only · speech ${p.speechBusy ?? 0} of ${p.speechLanes ?? 0} · $${p.perH.toFixed(2)}/h` : `${p.used} of ${p.slots} slots${p.speechLanes ? ` · speech ${p.speechBusy ?? 0} of ${p.speechLanes}` : ''} · $${p.perH.toFixed(2)}/h`;
  return (
    <div style={{ position: 'absolute', left: x, top: y, width: w, height: h, borderRadius: 10, border: `1px solid ${p.crashed && !gone ? 'var(--live)' : 'var(--vs-border)'}`, background: 'var(--fill-03)', opacity: gone ? 0.5 : 1, transition: 'top .4s' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px 0', height: POD_HEAD - 8 }}>
        <span style={{ fontSize: 12.5, fontWeight: 600 }}>{p.speechOnly ? 'Speech' : 'Pod'} {p.id}</span>
        <span style={{ fontSize: 11.5, color: 'var(--ink-55)' }}>{p.speechOnly ? `${p.host === 'runpod' ? 'RunPod' : 'Vast'} · ${p.placed ?? 'speech only'}` : p.gpu}{p.resumed ? ' · resumed' : ''}</span>
        <span style={{ fontSize: 11, fontWeight: 600, color: fg, display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 7, height: 7, borderRadius: '50%', background: fg }} />{label}</span>
        {why && <span title={why} style={{ fontSize: 11, color: 'var(--ink-55)', minWidth: 0, flex: '1 1 auto', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{why}</span>}
        <span style={{ flex: why ? '0 0 0' : 1 }} />
        {onKill && !gone && <button type="button" className="btn btn--xs btn--danger" title="The host dies silently, as a lost RunPod host does: no more heartbeats" onClick={onKill}>Kill</button>}
      </div>
      {Array.from({ length: p.slots }, (_, i) => (
        <div key={i} style={{ position: 'absolute', left: 12 + (i % cols) * SLOT + 2, top: POD_HEAD + Math.floor(i / cols) * SLOT + 2, width: SLOT - 4, height: SLOT - 4, borderRadius: 6, border: '1px dashed var(--fill-16)' }} />
      ))}
      <div style={{ position: 'absolute', left: 12, right: 12, bottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
        {(p.state === 'creating' || p.state === 'booting') && <div style={{ flex: '0 0 90px', height: 4, borderRadius: 2, background: 'var(--fill-08)' }}><div style={{ width: `${p.progress * 100}%`, height: '100%', borderRadius: 2, background: 'var(--brand)', transition: 'width .3s' }} /></div>}
        <span style={{ fontSize: 11, color: p.crashed || (p.broken && p.state === 'creating') ? 'var(--live)' : 'var(--ink-55)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{note}</span>
        {since && <span style={{ fontSize: 11, color: 'var(--ink-55)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginLeft: 'auto' }}>{since}</span>}
      </div>
    </div>
  );
}

export function FlowLegend() {
  const item = (style: React.CSSProperties, text: string) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}><span style={{ width: 12, height: 12, flex: 'none', ...style }} />{text}</span>
  );
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', fontSize: 11.5, color: 'var(--ink-62)' }}>
      {item({ borderRadius: '50%', background: 'var(--brand)', border: '2px solid var(--brand)' }, 'Multilingual live')}
      {item({ borderRadius: '50%', border: '2px solid var(--brand)' }, 'English live')}
      {item({ borderRadius: 3, background: 'var(--brand)', border: '2px solid var(--brand)' }, 'Multilingual file')}
      {item({ borderRadius: 3, border: '2px solid var(--brand)' }, 'English file')}
      {item({ borderRadius: '50%', border: '2px dashed var(--brand)', opacity: 0.55 }, 'not producing text yet')}
      {item({ borderRadius: '50%', border: '2px solid var(--live)', boxShadow: '0 0 0 3px var(--live-soft)' }, 'rescue from a lost pod')}
      {item({ borderRadius: 3, background: 'var(--conf-mid)', border: '2px solid var(--conf-mid)', boxShadow: '0 0 0 3px var(--fill-08)' }, 'file paused for a live session')}
      {item({ height: 0, borderTop: '2px dashed var(--brand)', width: 18 }, 'move in flight')}
    </div>
  );
}
